import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Copy, Share2, Gift, Users, Loader2, PiggyBank } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ReferralsTabProps {
  ptaId: string;
  ptaName: string;
}

const MAX_BANKED_MONTHS = 6;

export function ReferralsTab({ ptaId, ptaName }: ReferralsTabProps) {
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [piggyBankBalance, setPiggyBankBalance] = useState(0);
  const [totalRewarded, setTotalRewarded] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (ptaId) {
      fetchReferralData();
    }
  }, [ptaId]);

  const fetchReferralData = async () => {
    setLoading(true);
    try {
      // Fetch referral code
      const { data: codeData } = await supabase
        .from('pta_referral_codes')
        .select('code')
        .eq('pta_id', ptaId)
        .maybeSingle();

      if (codeData?.code) {
        setReferralCode(codeData.code);
      }

      // Fetch referrals
      const { data: referralsData } = await supabase
        .from('referrals')
        .select('*')
        .eq('referrer_pta_id', ptaId)
        .order('created_at', { ascending: false });

      setReferrals(referralsData || []);

      // Fetch piggy bank balance from subscription
      const { data: subData } = await supabase
        .from('subscriptions')
        .select('referral_months_balance')
        .eq('pta_id', ptaId)
        .in('status', ['active', 'trial'])
        .limit(1)
        .maybeSingle();

      setPiggyBankBalance(subData?.referral_months_balance ?? 0);

      // Fetch total rewarded months (historical)
      const { data: rewardsData } = await supabase
        .from('referral_rewards')
        .select('months_credited')
        .eq('pta_id', ptaId);

      const total = (rewardsData || []).reduce((sum, r) => sum + r.months_credited, 0);
      setTotalRewarded(total);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateCode = async () => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.rpc('generate_referral_code', {
        p_pta_id: ptaId,
      });

      if (error) throw error;
      setReferralCode(data);
      toast({ title: "Referral code generated!", description: `Your code is ${data}` });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const referralLink = referralCode
    ? `${window.location.origin}/onboarding?ref=${referralCode}`
    : '';

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied!", description: `${label} copied to clipboard.` });
  };

  const successfulReferrals = referrals.filter(r => ['activated', 'rewarded'].includes(r.status)).length;

  const statusBadge = (status: string) => {
    switch (status) {
      case 'pending': return <Badge variant="secondary">Pending</Badge>;
      case 'activated': return <Badge className="bg-blue-100 text-blue-800">Activated</Badge>;
      case 'rewarded': return <Badge className="bg-green-100 text-green-800">Rewarded</Badge>;
      case 'expired': return <Badge variant="outline">Expired</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Referral Code & Link */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Your Referral Link
          </CardTitle>
          <CardDescription>
            Share this link with other PTAs. When they sign up and subscribe, you earn free months saved in your piggy bank!
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {referralCode ? (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Referral Code</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded-md border bg-muted px-3 py-2 text-sm font-mono">
                    {referralCode}
                  </code>
                  <Button variant="outline" size="icon" onClick={() => copyToClipboard(referralCode, 'Referral code')}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Shareable Link</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded-md border bg-muted px-3 py-2 text-sm font-mono truncate">
                    {referralLink}
                  </code>
                  <Button variant="outline" size="icon" onClick={() => copyToClipboard(referralLink, 'Referral link')}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <Button onClick={generateCode} disabled={generating}>
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Share2 className="h-4 w-4 mr-2" />}
              Generate Referral Code
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Piggy Bank & Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="shadow-card md:col-span-1">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{successfulReferrals}</p>
                <p className="text-sm text-muted-foreground">Successful Referrals</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-card md:col-span-2">
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center gap-3">
              <PiggyBank className="h-8 w-8 text-primary" />
              <div className="flex-1">
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">{piggyBankBalance}</p>
                  <p className="text-sm text-muted-foreground">/ {MAX_BANKED_MONTHS} months banked</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {piggyBankBalance > 0
                    ? `You have ${piggyBankBalance} free month${piggyBankBalance !== 1 ? 's' : ''} saved — these will be applied at your next renewal`
                    : 'Refer other PTAs to start saving free months'}
                </p>
              </div>
            </div>
            <Progress value={(piggyBankBalance / MAX_BANKED_MONTHS) * 100} className="h-3" />
            {totalRewarded > 0 && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Gift className="h-3 w-3" />
                {totalRewarded} month{totalRewarded !== 1 ? 's' : ''} earned in total (including already redeemed)
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* How It Works */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PiggyBank className="h-5 w-5" />
            How It Works
          </CardTitle>
          <CardDescription>Earn free months on your subscription by referring other PTAs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border p-4 text-center">
              <p className="text-3xl font-bold text-primary">1</p>
              <p className="mt-1 font-semibold">Refer a PTA</p>
              <p className="text-sm text-muted-foreground mt-1">
                Share your link with another school's PTA
              </p>
            </div>
            <div className="rounded-lg border p-4 text-center">
              <p className="text-3xl font-bold text-primary">2</p>
              <p className="mt-1 font-semibold">They Subscribe</p>
              <p className="text-sm text-muted-foreground mt-1">
                1 free month is added to your piggy bank (max 6)
              </p>
            </div>
            <div className="rounded-lg border p-4 text-center">
              <p className="text-3xl font-bold text-primary">3</p>
              <p className="mt-1 font-semibold">Renewal Bonus</p>
              <p className="text-sm text-muted-foreground mt-1">
                Banked months are added to your renewal automatically
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referral History */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>Referral History</CardTitle>
          <CardDescription>Track all your referrals and their status</CardDescription>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No referrals yet. Share your link to get started!
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reward</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.map((referral) => (
                  <TableRow key={referral.id}>
                    <TableCell>
                      {new Date(referral.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{statusBadge(referral.status)}</TableCell>
                    <TableCell>
                      {referral.reward_months > 0
                        ? `${referral.reward_months} month${referral.reward_months > 1 ? 's' : ''} banked`
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
